import React, { useState } from 'react';

interface Category {
  id: number;
  name: string;
  slug: string;
  parent_id?: number;
  subcategories: Category[];
}

interface FeedOption {
  id: number;
  name: string;
  impact_factor: number | null;
  category_id: number;
  category_name: string | null;
}

interface OnboardingProps {
  token: string;
  categories: Category[];
  onComplete: () => void;
}

export default function Onboarding({ token, categories, onComplete }: OnboardingProps) {
  const [step, setStep] = useState(1);
  const [selectedTopLevel, setSelectedTopLevel] = useState<Set<number>>(new Set());
  const [subCategories, setSubCategories] = useState<Category[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<Set<number>>(new Set());
  const [feeds, setFeeds] = useState<FeedOption[]>([]);
  const [selectedFeeds, setSelectedFeeds] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(false);

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  // Top-level categories only
  const topLevel = categories.filter(c => !('parent_id' in c) || c.subcategories !== undefined);

  const toggleSet = (set: Set<number>, id: number): Set<number> => {
    const next = new Set(set);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  };

  // Collect all IDs from a category tree (the category + all descendants)
  const collectAllIds = (cat: Category): number[] => {
    let ids = [cat.id];
    for (const sub of cat.subcategories || []) {
      ids = ids.concat(collectAllIds(sub));
    }
    return ids;
  };

  const goToStep2 = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/onboarding/step1', {
        method: 'POST', headers,
        body: JSON.stringify({ category_ids: Array.from(selectedTopLevel) }),
      });
      const data: Category[] = await res.json();
      setSubCategories(data);

      // Pre-select all sub-category IDs (including the root) by default
      const allIds = new Set<number>();
      for (const root of data) {
        for (const id of collectAllIds(root)) {
          allIds.add(id);
        }
      }
      setSelectedCategories(allIds);
      setStep(2);
    } finally { setLoading(false); }
  };

  const goToStep3 = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/onboarding/step2', {
        method: 'POST', headers,
        body: JSON.stringify({ category_ids: Array.from(selectedCategories) }),
      });
      const data: FeedOption[] = await res.json();
      setFeeds(data);
      setSelectedFeeds(new Set(data.map(f => f.id)));
      setStep(3);
    } finally { setLoading(false); }
  };

  const finish = async () => {
    setLoading(true);
    try {
      await fetch('/api/onboarding/complete', {
        method: 'POST', headers,
        body: JSON.stringify({
          category_ids: Array.from(selectedCategories),
          feed_ids: Array.from(selectedFeeds),
        }),
      });
      onComplete();
    } finally { setLoading(false); }
  };

  const renderCheckbox = (id: number, label: string, set: Set<number>, toggle: (s: Set<number>, id: number) => Set<number>, setter: (s: Set<number>) => void, indent = 0) => (
    <label key={id} className="onboarding-checkbox" style={{ marginLeft: indent * 16 }}>
      <input type="checkbox" checked={set.has(id)} onChange={() => setter(toggle(set, id))} />
      <span>{label}</span>
    </label>
  );

  const toggleCategoryWithChildren = (set: Set<number>, cat: Category): Set<number> => {
    const next = new Set(set);
    if (next.has(cat.id)) {
      // Unchecking: remove this category and all descendants
      for (const id of collectAllIds(cat)) {
        next.delete(id);
      }
    } else {
      next.add(cat.id);
    }
    return next;
  };

  const renderCategoryTree = (cats: Category[], depth = 0) => {
    return cats.map(cat => (
      <React.Fragment key={cat.id}>
        <label key={cat.id} className="onboarding-checkbox" style={{ marginLeft: depth * 16 }}>
          <input type="checkbox" checked={selectedCategories.has(cat.id)}
            onChange={() => setSelectedCategories(toggleCategoryWithChildren(selectedCategories, cat))} />
          <span>{cat.name}</span>
        </label>
        {cat.subcategories && cat.subcategories.length > 0 && renderCategoryTree(cat.subcategories, depth + 1)}
      </React.Fragment>
    ));
  };

  return (
    <div className="onboarding-overlay">
      <div className="onboarding-card">
        <div className="onboarding-header">
          <h2 style={{ margin: 0, fontSize: 16 }}>set up your feed</h2>
          <div className="onboarding-steps">
            {[1, 2, 3].map(s => (
              <span key={s} className={`step-dot ${step === s ? 'active' : ''} ${step > s ? 'done' : ''}`} />
            ))}
          </div>
        </div>

        {step === 1 && (
          <div className="onboarding-body">
            <p style={{ fontSize: 12, color: '#888', marginBottom: 10 }}>
              Select the disciplines you're interested in:
            </p>
            <div className="checkbox-grid">
              {topLevel.map(cat =>
                renderCheckbox(cat.id, cat.name, selectedTopLevel, toggleSet, setSelectedTopLevel)
              )}
            </div>
            <div className="onboarding-actions">
              <button onClick={goToStep2} disabled={selectedTopLevel.size === 0 || loading}>
                {loading ? 'loading...' : 'next'}
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="onboarding-body">
            <p style={{ fontSize: 12, color: '#888', marginBottom: 10 }}>
              Refine your interests. All subcategories are selected by default — uncheck any you don't want:
            </p>
            <div className="checkbox-grid scrollable">
              {subCategories.map(root => (
                <div key={root.id} style={{ marginBottom: 12 }}>
                  <div style={{ fontWeight: 'bold', fontSize: 13, marginBottom: 4 }}>{root.name}</div>
                  {renderCategoryTree(root.subcategories, 0)}
                </div>
              ))}
            </div>
            <div className="onboarding-actions">
              <button onClick={() => setStep(1)} className="btn-secondary">back</button>
              <button onClick={goToStep3} disabled={loading}>
                {loading ? 'loading...' : 'next'}
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="onboarding-body">
            <p style={{ fontSize: 12, color: '#888', marginBottom: 10 }}>
              Choose which sources to follow. All are selected by default:
            </p>
            <div className="checkbox-grid scrollable">
              {feeds.map(f => (
                <label key={f.id} className="onboarding-checkbox">
                  <input type="checkbox" checked={selectedFeeds.has(f.id)}
                    onChange={() => setSelectedFeeds(toggleSet(selectedFeeds, f.id))} />
                  <span>
                    {f.name}
                    {f.impact_factor && <span style={{ color: '#888', fontSize: 10, marginLeft: 4 }}>IF: {f.impact_factor}</span>}
                    {f.category_name && <span style={{ color: '#5f99cf', fontSize: 10, marginLeft: 4 }}>({f.category_name})</span>}
                  </span>
                </label>
              ))}
            </div>
            <div className="onboarding-actions">
              <button onClick={() => setStep(2)} className="btn-secondary">back</button>
              <button onClick={finish} disabled={loading}>
                {loading ? 'saving...' : 'finish'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
